/** Admin page (/admin/games): game logs, newest first — stats, roster, and every move with bot reasoning. */
import { useEffect, useState } from 'react';
import type { GameLogDetail, GameLogEvent, GameLogSummary, PlayerId } from '@kadice/shared';
import { api } from '../api';

const when = (iso: string) => new Date(iso).toLocaleString();
const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const RESULT: Record<GameLogSummary['result'], string> = {
  winner: 'won',
  'no-survivors': 'no survivors',
  abandoned: 'abandoned',
  'in-progress': 'in progress',
};
/** Humans are marked, bots are named as such already. */
const who = (p: { name: string; bot: string | null }) => (p.bot ? p.name : `${p.name} (human)`);
const outcome = (g: GameLogSummary) => (g.result === 'winner' ? `${g.winnerName} won` : RESULT[g.result]);

export function GameLogAdmin() {
  const [list, setList] = useState<GameLogSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(
    () => Number(new URLSearchParams(location.search).get('id')) || null,
  );

  useEffect(() => {
    api.listGameLogs().then(setList, (err: unknown) => setError(err instanceof Error ? err.message : 'Failed.'));
  }, []);

  return (
    <div className="admin-feedback">
      <header className="row">
        <h2>Game logs</h2>
        <span className="spacer" />
        <a href="/admin/players">Players</a>
        <a href="/admin/feedback">Feedback reports</a>
        <a href="/">Back to game</a>
      </header>
      {error && <p className="error">{error}</p>}
      {list?.length === 0 && <p className="muted">No games logged yet.</p>}
      <div className="admin-feedback-body">
        <ul className="admin-feedback-list">
          {list?.map((g) => (
            <li key={g.gameId}>
              <button className={openId === g.gameId ? 'open' : ''} onClick={() => setOpenId(g.gameId)}>
                <span>
                  <strong>Game {g.gameId}</strong> · {outcome(g)}
                </span>
                <span className="muted small">
                  {g.players.map(who).join(', ')} · {g.rounds} round{g.rounds === 1 ? '' : 's'} · {when(g.startedAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {openId !== null && <Log key={openId} id={openId} />}
      </div>
    </div>
  );
}

function Log({ id }: { id: number }) {
  const [log, setLog] = useState<GameLogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getGameLog(id).then(setLog, (err: unknown) => setError(err instanceof Error ? err.message : 'Failed.'));
  }, [id]);

  if (error) return <p className="error">{error}</p>;
  if (!log) return <p className="muted">Loading…</p>;
  const name = (pid: PlayerId) => log.roster.find((p) => p.id === pid)?.name ?? `#${pid}`;
  const card = (cid: number) => log.cards[cid] ?? `card ${cid}`;
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: `kadice-game-${log.gameId}.json` });
    a.click();
    URL.revokeObjectURL(url);
  };
  const s = log.stats;

  return (
    <article className="admin-feedback-report">
      <h3>
        Game {log.gameId} · {outcome(log)}
      </h3>
      <p className="muted small">
        {when(log.startedAt)} · {clock(s.durationMs)} · {s.rounds} rounds · {s.wars} wars · {s.attacks} attacks ·{' '}
        {s.kills} kills · <button onClick={download}>Download JSON</button>
      </p>
      <div className="admin-gamelog-stats">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Player</th>
              <th title="Cards played">Cards</th>
              <th>Turns</th>
              <th>Attacks</th>
              <th title="Failed attacks">Missed</th>
              <th title="Kills (of which free kills)">Kills</th>
              <th title="Cards lost to attacks or wars">Lost</th>
              <th title="Wars won / fought">Wars</th>
              <th title="Turns or attacks that changed dice with a power">Powers</th>
            </tr>
          </thead>
          <tbody>
            {s.players.map((p) => (
              <tr key={p.id}>
                <td>{p.placement}</td>
                <td>
                  {who(p)}
                  {p.forfeited && <span className="muted small"> forfeited</span>}
                </td>
                <td>{p.cardsPlayed}</td>
                <td>{p.turns}</td>
                <td>{p.attacks}</td>
                <td>{p.failedAttacks}</td>
                <td>
                  {p.kills}
                  {p.freeKills > 0 && ` (${p.freeKills})`}
                </td>
                <td>{p.cardsLost}</td>
                <td>
                  {p.warsWon}/{p.warsFought}
                </td>
                <td>{p.powersUsed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details>
        <summary>Starting hands</summary>
        <ul>
          {log.roster.map((p) => (
            <li key={p.id}>
              Seat {p.seat + 1}, {who(p)} ({p.color}): {p.hand.map(card).join(', ')}
            </li>
          ))}
        </ul>
      </details>
      <ol className="admin-gamelog-events">
        {log.events.map((e, i) => (
          <li key={i} className={`ev-${e.type}`}>
            <span className="muted small">
              {clock(e.at)} R{e.round}.{e.seq}
            </span>{' '}
            {describe(e, name, card)}
          </li>
        ))}
      </ol>
    </article>
  );
}

const dice = (d: readonly number[]) => `[${d.join(' ')}]`;
const mods = (m: readonly { index: number; value: number }[]) =>
  m.length ? `, power: ${m.map((x) => `die ${x.index + 1}→${x.value}`).join(', ')}` : '';
const names = (ids: readonly PlayerId[], name: (id: PlayerId) => string) => ids.map(name).join(', ');

function describe(e: GameLogEvent, name: (id: PlayerId) => string, card: (id: number) => string): string {
  switch (e.type) {
    case 'start':
      return 'Game started.';
    case 'choose':
      return `${name(e.player)} chose ${card(e.card)}.`;
    case 'war':
      return `War over ${card(e.card)}: ${names(e.players, name)}.`;
    case 'warRoll':
      return `${name(e.player)} war roll ${dice(e.dice)} = ${e.dice.reduce((a, b) => a + b, 0)}.`;
    case 'warResult': {
      const lost = e.losers.length ? `; ${names(e.losers, name)} lost the card` : '';
      return e.winners.length === 1
        ? `${name(e.winners[0]!)} won the war${lost}.`
        : `Tie between ${names(e.winners, name)}${lost}.`;
    }
    case 'round':
      return `Round ${e.round}, dealer ${name(e.dealer)}.`;
    case 'turn':
      return `${name(e.player)}'s ${e.ghost ? 'ghost roll' : `turn${e.bonus ? ' (Full House bonus roll)' : e.extra ? ' (second turn)' : ''}`}.`;
    case 'roll':
      return `${name(e.player)} kept ${dice(e.kept)}, rolled ${dice(e.rolled)}.`;
    case 'endTurn':
      return `${name(e.player)} ended the turn holding ${dice(e.held)}${mods(e.mods)}${e.debug ? ' (debug)' : ''}.`;
    case 'attack': {
      const head = `${name(e.player)} attacked ${names(e.targets, name)} with ${dice(e.dice)}${mods(e.mods)}${e.debug ? ' (debug)' : ''}`;
      if (e.killed.length === 0) return `${head}: failed.`;
      const free = e.free.length ? ` (free kill: ${names(e.free, name)})` : '';
      const after = e.after === 'returned' ? ' Card returns to hand.' : e.after === 'bonus' ? ' Bonus roll.' : '';
      return `${head}: killed ${names(e.killed, name)}${free}.${after}`;
    }
    case 'wild':
      return `${name(e.player)} has five of a kind ${dice(e.dice)}${mods(e.mods)}: all dice wild.`;
    case 'out':
      return `${name(e.player)} is out.`;
    case 'ghostRoll':
      return e.matched === null
        ? `${name(e.player)} ghost rolled ${dice(e.dice)}: no match.`
        : `${name(e.player)} ghost rolled ${dice(e.dice)}: matched ${name(e.matched)}'s Kill Quest; ${e.card === null ? 'no card' : card(e.card)} back to hand.`;
    case 'forfeit':
      return `${name(e.player)} forfeited.`;
    case 'spectator':
      return `${e.name} ${e.joined ? 'started' : 'stopped'} watching.`;
    case 'reason':
      return `${name(e.player)} thinks: ${e.text}`;
    case 'deal': {
      const who = `${name(e.player)} → ${name(e.partner)}`;
      if (e.action === 'offer')
        return `${who}: deal offered (after ${e.target === undefined ? '?' : name(e.target)}, spare ${e.spare === 'pass' ? 'until the last pass' : 'the whole round'}).`;
      if (e.action === 'break') return `${who}: deal broken.`;
      return `${who}: ${e.action}${e.timeout ? ' (timed out)' : ''}.`;
    }
    case 'finish':
      return e.winner === null ? 'Game over, no winner.' : `${name(e.winner)} wins.`;
  }
}
